'use client'

import { useEffect } from 'react'

import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { useTracking } from '@marketplace-web/shared/event-tracker'

import { PersonalInformationSharingContentType } from 'constants/personal-information-sharing'
import { Screen } from 'constants/tracking/screens'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { clickEvent } from '_libs/common/event-tracker/events'

import EmailSharingModal from '../EmailSharingModal'

import PersonalInformationSharingModalVariantB from './PersonalInformationSharingModalVariantB'
import PersonalInformationSharingModalVariantC from './PersonalInformationSharingModalVariantC'

type Props = {
  threadId: string
  contentType: PersonalInformationSharingContentType
  onSetPersonalInformationSharingContentType: (
    contentType: PersonalInformationSharingContentType | null,
  ) => void
  onSendClick: () => void
}

const PersonalInformationSharingModalExperiment = ({
  threadId,
  contentType,
  onSetPersonalInformationSharingContentType,
  onSendClick,
}: Props) => {
  const { track } = useTracking()

  const abTest = useAbTest('personal_information_sharing_warning_v3')
  useTrackAbTest(abTest)

  useEffect(() => {
    track(
      clickEvent({
        screen: Screen.MessageReply,
        target: ClickableElement.PersonalInformationModalShow,
        targetDetails: JSON.stringify({
          thread_id: threadId,
          content_type: contentType,
        }),
      }),
    )
  }, [track, contentType, threadId])

  const handleLearnMoreClick = () => {
    track(
      clickEvent({
        screen: Screen.MessageReply,
        target: ClickableElement.PersonalInformationModalLearnMore,
        targetDetails: JSON.stringify({
          thread_id: threadId,
          content_type: contentType,
        }),
      }),
    )
  }

  const handleSendClick = () => {
    track(
      clickEvent({
        screen: Screen.MessageReply,
        target: ClickableElement.PersonalInformationModalSendAnyway,
        targetDetails: JSON.stringify({
          thread_id: threadId,
          content_type: contentType,
        }),
      }),
    )

    onSendClick()
    onSetPersonalInformationSharingContentType(null)
  }

  const handleEditClick = () => {
    track(
      clickEvent({
        screen: Screen.MessageReply,
        target: ClickableElement.PersonalInformationModalChangeResponse,
        targetDetails: JSON.stringify({
          thread_id: threadId,
          content_type: contentType,
        }),
      }),
    )

    onSetPersonalInformationSharingContentType(null)
  }

  if (abTest?.variant === 'b') {
    return (
      <PersonalInformationSharingModalVariantB
        contentType={contentType}
        onEditClick={handleEditClick}
        onSendClick={handleSendClick}
        onLearnMoreClick={handleLearnMoreClick}
      />
    )
  }

  if (abTest?.variant === 'c') {
    return (
      <PersonalInformationSharingModalVariantC
        contentType={contentType}
        onEditClick={handleEditClick}
        onSendClick={handleSendClick}
        onLearnMoreClick={handleLearnMoreClick}
      />
    )
  }

  return (
    <EmailSharingModal
      onEditClick={handleEditClick}
      onSendClick={handleSendClick}
      onLearnMoreClick={handleLearnMoreClick}
    />
  )
}

export default PersonalInformationSharingModalExperiment
